import React, { useEffect, useState, useRef } from 'react';
import '../../styles/onboardingFlow/onboarding/onboardingIntegration.scss';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { useTranslation } from 'react-i18next';
import MainSquareButton from '../../ui/buttons/MainSquareButton';
import {
  setAiOnboardingNextStep,
  setAiOnboardingStepCompleted,
  clearOnboardingOAuthCallbackParams,
} from '../../store/reducers/userSlice';
import save_mcp_oauth_api_call from '../../lib/api/mcp/save-mcp-oauth';
import update_all_shop_stats_api_call from '../../lib/api/dashboard/update_all_shop_stats_api_call';
import IntegrationCard from '../onboardingComponents/IntegrationCard';
import IntegrationModal from '../onboardingComponents/IntegrationModal';
import LoadingSpinner from '../../ui/loadingSpinner/LoadingSpinner';
import get_all_available_mcp_api_call from '../../lib/api/mcp/get-all-available-mcp';
import get_all_connected_mcp_api_call from '../../lib/api/mcp/get-all-connected-mcp';
import makeFirstLetterCapital from '../../lib/helper-functions/makeFirstLetterCapital';
import isNotEmptyString from '../../lib/helper-functions/isNotEmptyString';
import { submitLogToBugsnag } from '../../lib/api/log';
import store from '../../store/store';
import { setMessage } from '../../store/reducers/appSlice';
import mcpImg from '../../assets/mcp.png';
import PlugsIcon from '../../assets/onboarding/common/Plugs.svg';
import OnboardingLogoPanel from '../onboardingComponents/OnboardingLogoPanel';
import OnboardingFooter from '../onboardingComponents/OnboardingFooter';

// Get description for each integration based on MCP slug
const getIntegrationDescription = (mcpSlug: string): string => {
  const descriptions: { [key: string]: string } = {
    shopify:
      'Connect your store to track sales, products, and customer activity in real time.',
    intercom:
      'Sync conversations and user data to improve customer engagement and support insights.',
    woocommerce:
      'Link your eCommerce site to analyze orders, products, and revenue performance effortlessly.',
    stripe:
      'Connect payment data to view transactions, revenue trends, and recurring customer activity.',
    hubspot:
      'Integrate CRM and marketing data for better lead tracking and automation insights.',
    google_search_console:
      'Import SEO and keyword performance data to monitor search visibility and clicks.',
    google_ads:
      'Monitor campaigns, clicks, and conversions directly from your connected ad account.',
    google_analytics:
      'Access traffic, conversion, and behavior metrics from your websites in one place.',
    facebook_ads:
      'Connect your Facebook Ads account to track campaign performance and ad spend.',
    tiktok_ads:
      'Connect your TikTok Ads account to monitor video ad performance and engagement.',
    linkedin_ads:
      'Integrate LinkedIn Ads data to track B2B campaign performance and lead generation.',
  };
  return (
    descriptions[mcpSlug] || 'Connect this integration to sync data with TARA.'
  );
};

const OnboardingIntegration: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [mcpList, setMcpList] = useState<any[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedMCP, setSelectedMCP] = useState<any>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // whole querystring saved in Redux by App (e.g. "?code=...&state=..."),
  // or null if not set
  const onboardingOAuthCallback = useSelector(
    (state: RootState) => state.user.ai_onboarding.onboardingOAuthCallback
  );

  const processedOAuthRef = useRef(false);

  const getMCPs = async (
    noLoader: boolean = false,
    errorMcpId: number | undefined = undefined
  ) => {
    function processData(allMCPs: [], connectedMCPs: []) {
      const connectionMap = new Map();
      connectedMCPs.forEach((item: any) => {
        connectionMap.set(item.tara_mcp_id, item);
      });

      let hasActiveNonDefault = false;
      const processedData = allMCPs.map((item: any) => {
        const mcpConnectionId = connectionMap.get(item.id)?.id;
        const isAdUpAITracking =
          connectionMap.get(item.id)?.tara_mcp?.slug === 'adup_ai_tracking';
        const isActive = connectionMap.get(item.id)?.is_active;
        const isDefault = item?.is_default;

        const formattedMCP = {
          id: item?.id,
          mcp_slug: item?.slug,
          mcp_name: item?.mcp_name,
          credentials_fields:
            item?.slug === 'google_ads'
              ? [...item?.credentials_fields, 'MANAGER_ACCOUNT_ID']
              : item?.credentials_fields,
          logo: item?.logo,
          connection_id: mcpConnectionId,
          credentials: connectionMap.get(item.id)?.credentials,
          is_active: isActive,
          is_valid: connectionMap.get(item.id)?.is_valid ?? true,
          is_default: isDefault,
          configs: item?.configs,
          oauth_url: item?.oauth_url,
          oauth_required: connectionMap.get(item.id)?.oauth_required,
          accounts: connectionMap.get(item.id)?.accounts,
          isAdUpAITracking,
        };

        if (isDefault === false && isActive === true) {
          hasActiveNonDefault = true;
        }

        return formattedMCP;
      });

      return processedData;
    }

    try {
      setIsLoading(!noLoader);
      const allMCPs = await get_all_available_mcp_api_call();
      const connectedMCPs = await get_all_connected_mcp_api_call();
      const data = processData(allMCPs, connectedMCPs);
      setMcpList(data);
      setIsLoading(false);
      if (errorMcpId) {
        const mcp = data.find((m: any) => m?.connection_id === errorMcpId);
        if (mcp) {
          oAuthRedirectHandler(mcp);
        }
      }
    } catch (error) {
      setIsLoading(false);
      console.error('Error fetching data:', error);
      submitLogToBugsnag('error', `Error in fetching MCP data: ${error}`);
    }
  };

  // OAuth callback handling: prefer Redux querystring; fallback to current URL.
  useEffect(() => {
    if (processedOAuthRef.current) return;
    const search =
      typeof onboardingOAuthCallback === 'string' && onboardingOAuthCallback
        ? onboardingOAuthCallback
        : window.location.search;

    const params = new URLSearchParams(search);
    const code = params.get('code') || params.get('auth_code'); // support both
    const state = params.get('state');

    if (code && isNotEmptyString(code) && state && isNotEmptyString(state)) {
      processedOAuthRef.current = true;
      // clear URL first (remove query noise from address bar)
      window.history.replaceState({}, '', window.location.pathname);
      // clear redux copy if present
      if (onboardingOAuthCallback) {
        dispatch(clearOnboardingOAuthCallbackParams());
      }
      // optional tiny log to confirm we saw it once
      console.log('[Onboarding OAuth] callback detected & consumed');
      oAuthSaveHandler(code, state);
    } else {
      getMCPs();
    }
    // re-run if Redux gets populated after mount (but guard with ref)
  }, [onboardingOAuthCallback]); // eslint-disable-line react-hooks/exhaustive-deps

  const constructDynamicOauthURL = (
    providerOauthURL: string,
    payload: object
  ) => {
    // @ts-ignore
    const STATE = btoa(JSON.stringify(payload));
    const REDIRECT_URI = `${window.location.origin}/onboarding`;
    const URL = `${providerOauthURL}&redirect_uri=${REDIRECT_URI}&state=${STATE}`;
    return URL;
  };

  const oAuthRedirectHandler = (
    mcp: any,
    isToggleClick: boolean = false,
    toggleStatus: boolean = false
  ) => {
    const mode = mcp?.is_active !== undefined ? 'update' : 'add';

    const isOauth =
      mcp?.oauth_url &&
      isNotEmptyString(mcp?.oauth_url) &&
      (mode === 'update' ? mcp?.oauth_required : true);

    if (isOauth) {
      try {
        setIsSubmitting(true);
        const redirectURL = constructDynamicOauthURL(mcp?.oauth_url, mcp);
        window.location.href = redirectURL;
      } catch (error) {
        setIsSubmitting(false);
        console.error(
          `${mcp?.configs?.oauth?.provider} OAuth - Error connecting MCP:`,
          error
        );
        submitLogToBugsnag(
          'error',
          `${mcp?.configs?.oauth?.provider} OAuth - Error in connecting MCP: ${error}`
        );
        store.dispatch(
          setMessage({
            message: `${t('MCP-OauthFailed')}`,
            messageType: 'error',
          })
        );
      }
    } else {
      if (isToggleClick) {
        // For toggle clicks, we'd need to handle status toggle
        // But in onboarding, we don't have toggle functionality
        setSelectedMCP(mcp);
        setIsModalOpen(true);
      } else {
        setSelectedMCP(mcp);
        setIsModalOpen(true);
      }
    }
  };

  const oAuthSaveHandler = async (code: string, state: string) => {
    try {
      // @ts-ignore
      const decodedState = JSON.parse(atob(state));
      const mcp = { ...decodedState, oauthCode: code };

      const response = await save_mcp_oauth_api_call(
        mcp.id,
        mcp.oauthCode,
        true
      );

      await getMCPs();

      if (response?.success) {
        if (mcp?.credentials_fields?.length > 0) {
          setSelectedMCP({
            ...mcp,
            accounts: response?.data?.accounts,
          });
          setIsModalOpen(true);
        } else {
          // No credentials fields needed, connection is complete
          // Modal will be handled by getMCPs refresh
        }
      } else {
        setSelectedMCP(mcp);
        setIsModalOpen(true);
        store.dispatch(
          setMessage({
            message: isNotEmptyString(response?.message)
              ? response?.message
              : `${t('MCP-OauthFailed')}`,
            messageType: isNotEmptyString(response?.message)
              ? 'warning'
              : 'error',
          })
        );
      }
    } catch (error) {
      console.error(`OAuth - Failed to decode state:`, error);
      submitLogToBugsnag('error', `OAuth - Failed to decode state: ${error}`);
    }
  };

  const handleCardClick = (mcp: any) => {
    oAuthRedirectHandler(mcp);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedMCP(null);
    setIsSubmitting(false);
  };

  const activeMCPsCount = mcpList.filter(
    (mcp) => mcp.is_active === true && mcp.is_valid
  ).length;
  const minRequired = 1;

  const handleContinue = async () => {
    if (activeMCPsCount >= minRequired) {
      const sendOnboardingStatus = await update_all_shop_stats_api_call(
        5,
        false
      );
      if (!sendOnboardingStatus) {
        throw new Error('Onboarding status submission failed');
      }
      dispatch(
        setAiOnboardingStepCompleted({
          stepNumber: 4,
          isCompleted: true,
        })
      );
      dispatch(setAiOnboardingNextStep(5));
    }
  };

  return (
    <div className='onboardingIntegration'>
      <OnboardingLogoPanel />
      <div className='onboardingIntegration__header'>
        <div className='onboardingIntegration__header-row'>
          <div className='onboardingIntegration__header-icon'>
            <img src={PlugsIcon} alt='Connect' />
          </div>
          <h1 className='onboardingIntegration__title'>
            {t('ConnectYourIntegrations')}
          </h1>
        </div>
        <p className='onboardingIntegration__subtitle'>
          {t('ConnectYourIntegrationsSubtitle')}
        </p>
      </div>

      <div className='onboardingIntegration__content'>
        {isLoading ? (
          <LoadingSpinner />
        ) : (
          <div className='onboardingIntegration__grid'>
            {mcpList.map((mcp) => (
              <IntegrationCard
                key={mcp.id}
                mcp={mcp}
                onClick={() => handleCardClick(mcp)}
                image={isNotEmptyString(mcp?.logo) ? mcp?.logo : mcpImg}
                title={makeFirstLetterCapital(mcp.mcp_name)}
                description={getIntegrationDescription(mcp.mcp_slug)}
                isActive={mcp.is_active === true}
                hasOAuth={isNotEmptyString(mcp?.oauth_url)}
                isInvalid={
                  !mcp?.is_default && !mcp?.is_valid && mcp.is_active === true
                }
                needsOAuthConsent={
                  !mcp?.is_default &&
                  mcp?.oauth_required &&
                  isNotEmptyString(mcp?.oauth_url) &&
                  mcp.is_active === true
                }
              />
            ))}
          </div>
        )}
      </div>

      <div className='onboardingIntegration__footer'>
        <div className='onboardingIntegration__footer-content'>
          <p className='onboardingIntegration__footer-text'>
            {t('TARANeedsAccessToYourApps')}
          </p>
          <p className='onboardingIntegration__footer-requirement'>
            {t('ConnectAtLeast2PlatformsToContinue')}{' '}
            <span className='onboardingIntegration__footer-count'>
              [{activeMCPsCount} {t('PlatformsConnected')}]
            </span>
          </p>
        </div>
        <div className='onboardingIntegration__footer-buttons'>
          <button
            type='button'
            className='onboardingIntegration__button onboardingIntegration__button--secondary'
            onClick={() => dispatch(setAiOnboardingNextStep(2))}
          >
            {t('GoBack')}
          </button>
          <MainSquareButton
            type='button'
            value={t('Continue')}
            normal={true}
            disabled={activeMCPsCount < minRequired || isSubmitting}
            onClick={handleContinue}
            classes='onboardingIntegration__button--primary'
          />
        </div>
      </div>

      <OnboardingFooter />

      {isModalOpen && selectedMCP && (
        <IntegrationModal
          isOpen={isModalOpen}
          onClose={handleModalClose}
          mcp={selectedMCP}
          onUpdate={getMCPs}
        />
      )}
    </div>
  );
};

export default OnboardingIntegration;
